/**
 * Description: Public orchestrator for the digital pet: config merge, engines, lifecycle, rAF loop.
 * Usage: const pet = new NanoNomad({ host, runtimeConfig }); await pet.start(); pet.pause(); pet.destroy();
 */
import { createBrowserHost } from './host.js';
import { CapabilityRegistry } from './capability-registry.js';
import { registerBuiltinCapabilities } from './register-builtins.js';
import { Environment } from './environment.js';
import { MovementEngine } from './movement-engine.js';
import { BehaviorEngine } from './behavior-engine.js';
import { Renderer } from './renderer.js';
import { mergeRuntimeConfig } from './config-manager.js';

const MOBILE_BREAKPOINT = 768;
const IDLE_TIMEOUT = 60000;
const SCROLL_FADE_OPACITY = 0.3;
const PERSIST_INTERVAL = 5000;
const PERSIST_KEY = 'ndp_state';
const MAX_FRAME_DT = 50;

export class NanoNomad {
  /**
   * Description: Constructs an engine instance without starting the loop (safe for SSR if no start()).
   * Usage: Options: host, runtimeConfig, fetch, capabilityRegistry, mountTarget (HTMLElement), registerBuiltin (default true).
   */
  constructor(options = {}) {
    this._options = options;
    this._host = options.host || null;
    this._fetch = options.fetch || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
    this._mountTarget = options.mountTarget || null;
    this._registry = options.capabilityRegistry || new CapabilityRegistry();
    if (options.registerBuiltin !== false) {
      registerBuiltinCapabilities(this._registry);
    }

    this._config = null;
    this._environment = null;
    this._movement = null;
    this._behavior = null;
    this._renderer = null;

    this._running = false;
    this._rafId = null;
    this._lastTime = 0;
    this._idleTimer = null;
    this._userIdle = false;
    this._scrollTimer = null;
    this._persistTimer = null;

    this._onVisibility = null;
    this._onScroll = null;
    this._onMouse = null;
    this._onKey = null;
    this._onTouch = null;
    this._onBeforeUnload = null;
  }

  static createBrowserHost(globalObj) {
    return createBrowserHost(globalObj);
  }

  _isMobile() {
    return this._host.window.innerWidth < MOBILE_BREAKPOINT;
  }

  _gameLoop = (timestamp) => {
    if (!this._running) return;

    const dt = this._lastTime ? Math.min(timestamp - this._lastTime, MAX_FRAME_DT) : 16;
    this._lastTime = timestamp;

    this._environment.update();
    this._behavior.update(dt);
    this._movement.update(dt / 1000);

    const pos = this._movement.getPosition();
    const state = this._behavior.getState();
    const dir = this._movement.getFacing();
    this._renderer.update(pos.x, pos.y, state, dir);

    this._rafId = this._host.window.requestAnimationFrame(this._gameLoop);
  };

  _startLoop() {
    if (this._running) return;
    this._running = true;
    this._lastTime = 0;
    this._rafId = this._host.window.requestAnimationFrame(this._gameLoop);
  }

  _stopLoop() {
    this._running = false;
    if (this._rafId) {
      this._host.window.cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _resetIdleTimer = () => {
    if (this._userIdle) {
      this._userIdle = false;
      this._movement.resume();
      this._behavior.resume();
      this._renderer.setOpacity(1);
      this._startLoop();
    }
    const win = this._host.window;
    if (this._idleTimer) win.clearTimeout(this._idleTimer);
    this._idleTimer = win.setTimeout(() => {
      this._userIdle = true;
      this._behavior.pause();
      this._movement.pause();
      this._stopLoop();
    }, IDLE_TIMEOUT);
  };

  _handleVisibility = () => {
    const doc = this._host.document;
    if (doc.hidden) {
      this._stopLoop();
      this._behavior.pause();
      this._movement.pause();
    } else {
      this._movement.resume();
      this._behavior.resume();
      this._startLoop();
    }
  };

  _handleScroll = () => {
    this._renderer.setOpacity(SCROLL_FADE_OPACITY);
    const win = this._host.window;
    if (this._scrollTimer) win.clearTimeout(this._scrollTimer);
    this._scrollTimer = win.setTimeout(() => {
      if (!this._userIdle) this._renderer.setOpacity(1);
    }, 400);
  };

  _persistState = () => {
    if (!this._config || !this._config.persistenceEnabled) return;
    const win = this._host.window;
    if (!win.localStorage) return;
    try {
      const pos = this._movement.getPosition();
      win.localStorage.setItem(
        PERSIST_KEY,
        JSON.stringify({
          x: Math.round(pos.x),
          y: Math.round(pos.y),
          state: this._behavior.getState(),
          ts: Date.now(),
        })
      );
    } catch {
      /* quota */
    }
  };

  _bindLifecycleEvents() {
    const doc = this._host.document;
    const win = this._host.window;

    doc.addEventListener('visibilitychange', this._handleVisibility);
    win.addEventListener('scroll', this._handleScroll, { passive: true });
    doc.addEventListener('mousemove', this._resetIdleTimer, { passive: true });
    doc.addEventListener('keydown', this._resetIdleTimer, { passive: true });
    doc.addEventListener('touchstart', this._resetIdleTimer, { passive: true });
    this._resetIdleTimer();
  }

  _unbindLifecycleEvents() {
    const doc = this._host.document;
    const win = this._host.window;
    doc.removeEventListener('visibilitychange', this._handleVisibility);
    win.removeEventListener('scroll', this._handleScroll);
    doc.removeEventListener('mousemove', this._resetIdleTimer);
    doc.removeEventListener('keydown', this._resetIdleTimer);
    doc.removeEventListener('touchstart', this._resetIdleTimer);
    if (this._idleTimer) win.clearTimeout(this._idleTimer);
    if (this._scrollTimer) win.clearTimeout(this._scrollTimer);
    this._idleTimer = null;
    this._scrollTimer = null;
  }

  _startPersistence() {
    if (!this._config || !this._config.persistenceEnabled) return;
    const win = this._host.window;
    this._persistTimer = win.setInterval(this._persistState, PERSIST_INTERVAL);
    this._onBeforeUnload = this._persistState;
    win.addEventListener('beforeunload', this._onBeforeUnload);
  }

  _stopPersistence() {
    const win = this._host.window;
    if (this._persistTimer) {
      win.clearInterval(this._persistTimer);
      this._persistTimer = null;
    }
    if (this._onBeforeUnload) {
      win.removeEventListener('beforeunload', this._onBeforeUnload);
      this._onBeforeUnload = null;
    }
  }

  /**
   * Description: Loads config, mounts DOM, wires engines, and starts the animation loop.
   * Usage: await pet.start(); requires host set (or pass in constructor).
   */
  async start() {
    if (!this._host) {
      this._host = createBrowserHost(typeof window !== 'undefined' ? window : null);
    }

    const runtime = this._options.runtimeConfig || {};
    this._config = await mergeRuntimeConfig(runtime, { fetch: this._fetch });

    if (!this._config.mobileEnabled && this._isMobile()) {
      return;
    }

    if (!this._fetch) {
      throw new Error('NanoNomad: fetch is required to load character assets');
    }

    this._environment = new Environment(this._host);
    this._movement = new MovementEngine(this._environment, this._host);
    this._behavior = new BehaviorEngine(this._registry);
    this._renderer = new Renderer(this._host, this._fetch);

    await this._renderer.init(this._config, this._mountTarget);

    this._environment.init();
    this._movement.init(this._config);
    this._behavior.init({
      config: this._config,
      movement: this._movement,
      environment: this._environment,
    });

    this._bindLifecycleEvents();
    this._startPersistence();
    this._startLoop();
  }

  /**
   * Description: Stops rAF and input listeners; does not remove DOM (use destroy).
   */
  pause() {
    this._stopLoop();
    this._behavior.pause();
    this._movement.pause();
  }

  /**
   * Description: Resumes after pause(); noop if not initialized.
   */
  resume() {
    if (!this._behavior || !this._movement) return;
    this._movement.resume();
    this._behavior.resume();
    this._startLoop();
  }

  /**
   * Description: Tears down timers, listeners, DOM, and engine references.
   */
  destroy() {
    this._stopLoop();
    this._stopPersistence();
    this._unbindLifecycleEvents();
    if (this._environment) {
      this._environment.destroy();
      this._environment = null;
    }
    if (this._renderer) {
      this._renderer.destroy();
      this._renderer = null;
    }
    this._movement = null;
    this._behavior = null;
    this._config = null;
  }

  getConfig() {
    return this._config;
  }
}
